import { useEffect, useRef, useState } from 'react';

export const useStylePropertyVariableValueFromParent = (
	cssVariableName: string,
): [React.RefObject<HTMLElement>, string | undefined] => {
	const ref = useRef<HTMLElement>(null);
	const [propertyValue, setPropertyValue] = useState<string | undefined>();

	useEffect(() => {
		if (!ref.current) {
			return;
		}

		let element: HTMLElement | null = ref.current;
		let depth = 0;
		const maxDepth = 20; // prevent infinite loops

		while (element && depth < maxDepth) {
			let computedStyle: CSSStyleDeclaration;
			try {
				computedStyle = getComputedStyle(element);
			} catch {
				// jsdom/nwsapi can throw a SyntaxError when evaluating CSS selectors
				// that contain characters valid in HTML id attributes but not in CSS
				// (e.g. React's useId() values like ":r0:" combined with :has() rules).
				// In real browsers this never throws — degrade gracefully.
				break;
			}
			const value = computedStyle.getPropertyValue(cssVariableName).trim();

			if (value && value !== '' && value !== 'transparent') {
				setPropertyValue(value);
				return;
			}

			element = element.parentElement;
			depth++;
		}

		setPropertyValue(undefined);
	}, [cssVariableName]);

	return [ref, propertyValue];
};
